import type { TFunction } from 'i18next'

import type { ExternalModbusClientReference } from '@/features/controller/configuration/models/raspiProperties/types'

const FAMILY_LABELS: Record<string, (t: TFunction) => string> = {
  metering: (t) => t('controllers.configuration.device_connections.external_device.family.metering'),
  nwBattery: (t) => t('controllers.configuration.device_connections.external_device.family.nwBattery'),
  steering: (t) => t('controllers.configuration.device_connections.external_device.family.steering'),
}

/** A battery asset already exists for this, drawn at icon scale, in `public/resource-types`. */
export const NW_BATTERY_ICON_URL = '/resource-types/battery.svg'

/**
 * Which icon a family gets. Kept here with the family's other presentation so there is one place to
 * look, rather than resolved to an element, which would pull MUI into a util module.
 */
export enum ExternalModbusDeviceIcon {
  battery = 'battery',
  meter = 'meter',
  steering = 'steering',
  /** For a family with nothing more fitting, including one not modelled here at all. */
  generic = 'generic',
}

export function getExternalModbusDeviceIcon(family: string): ExternalModbusDeviceIcon {
  switch (family) {
    case 'nwBattery':
      return ExternalModbusDeviceIcon.battery
    case 'metering':
      return ExternalModbusDeviceIcon.meter
    case 'steering':
      return ExternalModbusDeviceIcon.steering
    default:
      return ExternalModbusDeviceIcon.generic
  }
}

/**
 * How a device attached to a named connection is named in the UI. A family with several members is
 * qualified by the id its operator chose — "Metering: grid-import" — while a singleton family stands
 * alone. Property keys are deliberately not shown: the raw properties editor is where those belong.
 *
 * Falls back to the raw family name so a configuration not modelled here is still named rather than
 * hidden, which is the lesser evil of the two.
 */
export function describeExternalModbusDevice(reference: ExternalModbusClientReference, t: TFunction) {
  const family = FAMILY_LABELS[reference.family]?.(t) ?? reference.family
  return reference.deviceId ? `${family}: ${reference.deviceId}` : family
}
